import { useState } from "react";
import { Link } from "react-router-dom";
import { ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { Checkbox } from "@/components/ui/checkbox";
import { api, errMsg } from "@/lib/api";
import { POLICIES } from "@/lib/policies";

const REQUIRED = [
  ["terms", "Terms of Service", "/legal/terms", "I have read and agree to the Terms of Service. I understand that AI-generated replies are suggestions, and I am responsible for reviewing messages before sending them."],
  ["privacy", "Privacy Policy", "/legal/privacy", "I have read the Privacy Policy and understand how my submitted screenshots, conversation text, account information, and AI requests may be processed."],
  ["community", "Community Guidelines", "/legal/community", "I agree to use this app respectfully. I will not use it to harass, threaten, impersonate, manipulate, stalk, or pressure another person, or to continue unwanted romantic contact after someone has expressed a clear boundary."],
  ["age", "Age requirement", "/legal/terms", "I confirm that I meet the minimum age requirement specified in the published Terms of Service and applicable law."],
  ["ai_processing", "AI processing notice", "/legal/privacy", "I understand that the text or screenshots I submit may be processed by third-party AI or OCR providers to generate suggestions, subject to the app's disclosed privacy practices."],
];

const PolicyText = () => (
  <div data-testid="policy-scroll-box" className="max-h-64 overflow-y-auto rounded-2xl border border-border bg-secondary/30 p-5 space-y-5 text-sm">
    {["terms", "privacy", "community"].map((k) => (
      <div key={k} className="space-y-2">
        <h3 className="font-semibold">{POLICIES[k].title} <span className="text-xs text-muted-foreground font-normal">v{POLICIES[k].version}</span></h3>
        {POLICIES[k].body.map((p) => <p key={p.slice(0, 40)} className="text-muted-foreground leading-relaxed">{p}</p>)}
      </div>
    ))}
  </div>
);

export const PoliciesStep = ({ onboarding, onDone }) => {
  const [checks, setChecks] = useState({});
  const [marketing, setMarketing] = useState(false);
  const [busy, setBusy] = useState(false);
  const all = REQUIRED.every(([k]) => checks[k]);
  const renewal = onboarding.missing_policies.length < REQUIRED.length;

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/onboarding/consents", { ...checks, marketing, policy_versions: onboarding.policy_versions });
      toast.success("All set — welcome!");
      onDone(data);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="panel p-6 sm:p-8 space-y-6 rise" data-testid="policies-step">
      <div className="space-y-2">
        <h1 className="text-3xl font-extrabold tracking-tight">{renewal ? "We've updated our policies" : "Before you start"}</h1>
        <p className="text-sm text-muted-foreground">Please read and accept each item. You must be {onboarding.min_age}+ to use this app.</p>
      </div>
      <PolicyText />
      <div className="space-y-2">
        {REQUIRED.map(([k, title, href, text]) => (
          <div key={k} className={`flex items-start gap-3 rounded-2xl border p-4 transition-colors ${checks[k] ? "border-primary/60 bg-primary/5" : "border-border"}`}>
            <Checkbox id={`consent-${k}`} data-testid={`consent-checkbox-${k}`} checked={!!checks[k]} onCheckedChange={(c) => setChecks((s) => ({ ...s, [k]: !!c }))} className="mt-0.5" />
            <label htmlFor={`consent-${k}`} className="text-sm leading-relaxed flex-1 cursor-pointer">
              <span className="block font-semibold mb-0.5">{title} <span className="text-rose-500">*</span></span>{text}
            </label>
            <Link to={href} target="_blank" data-testid={`consent-link-${k}`} className="shrink-0 text-primary" aria-label={`Read ${title}`}><ExternalLink className="h-4 w-4" /></Link>
          </div>
        ))}
      </div>
      <div className="rounded-2xl border border-dashed border-border p-4 flex items-start gap-3">
        <Checkbox id="consent-marketing" data-testid="consent-checkbox-marketing" checked={marketing} onCheckedChange={(c) => setMarketing(!!c)} className="mt-0.5" />
        <label htmlFor="consent-marketing" className="text-sm cursor-pointer"><span className="font-semibold">Optional:</span> Send me occasional product updates by email. Not required to use the app.</label>
      </div>
      <button data-testid="agree-continue-button" disabled={!all || busy} onClick={submit}
        className="w-full h-12 rounded-full brand-gradient text-white font-semibold disabled:opacity-40 transition-opacity">
        {busy ? "Saving…" : "Agree & Continue"}
      </button>
    </section>
  );
};
